/* @flow */

import { apiService } from 'services/ServiceRegistry';
import { Roles } from 'permissions/defs';

const password: Object = {
  value: '',
  minLength: 8,
};

const leapYear: Function = (year: string) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const isValidDate: Function = (inDate: string) => {
  const testDate: Date = new Date(inDate);
  const yr: string = testDate.getFullYear();
  const mo: string = testDate.getMonth();
  const day: string = testDate.getDate();
  const daysInMonth: Array = [31, leapYear(yr) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (yr < 1000) {
    return false;
  }

  if (isNaN(mo)) {
    return false;
  }

  if (mo + 1 > 12) {
    return false;
  }

  if (isNaN(day)) {
    return false;
  }

  if (day > daysInMonth[mo]) {
    return false;
  }

  return true;
};

const asyncRules: Object = {
  validEmailDomain: async function(val, attr, req, passes) {
    const v: Object = this.validator;
    const m1: string = 'The Email address domain is not valid. Please use another Email address.';
    const m2: string = 'The Email address format is invalid.';

    if (v.getRule('email').validate(val)) {
      try {
        const url: string = `users/validate-email?email=${encodeURIComponent(val)}`;
        const response: * = await apiService.getResource(url);

        if (response.is_valid) {
          passes();
        } else {
          passes(false, m1);
        }
      } catch (err) {
        passes(false, m2);
      }
    } else {
      passes(false, m2);
    }
  },
};

const rules: Object = {
  telephone: {
    //function: (value: string) => value.match(/^\d{3}-\d{3}-\d{4}$/),
    function: (value: string) => {
      // https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript
      // eslint-disable-next-line no-useless-escape
      const validRegex: RegExp = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/g;
      return value.match(validRegex);
    },
    message: 'Please enter a valid :attribute.',
  },

  password: {
    function: (value: string) => {
      password.value = value;
      return value.length >= password.minLength;
    },
    message: `Please type a pasword that is at least ${password.minLength} characters long`,
  },

  confirmPassword: {
    function: (confirmPassword: string) => password.value === confirmPassword,
    message: "Password and Change Password doesn't match",
  },

  validBusinessEmail: {
    function: (value: string) => {
      const validRegex: RegExp = /(aol|gmail|yahoo|hotmail)\.com$/;
      return !validRegex.test(String(value).toLowerCase());
    },
    message: 'Please enter a valid email address',
  },

  // For checking date is at least one day in the future
  in_future: {
    function: (val1: string) => {
      if (!isValidDate(val1)) {
        return false;
      }

      if (new Date().getTime() <= new Date(val1).getTime()) {
        return true;
      }

      return false;
    },
    message: 'The :attribute must be in the future.',
  },

  // Bugs found in validatorjs rules 'after' and 'after_or_equal' so overriding them here
  // Issued raised - https://github.com/skaterdav85/validatorjs/issues/279
  // https://github.com/skaterdav85/validatorjs/blob/master/src/rules.js
  after: {
    function: (val1: string, val2: string) => {
      if (!isValidDate(val1)) {
        return false;
      }

      if (!isValidDate(val2)) {
        return false;
      }

      if (new Date(val2).getTime() < new Date(val1).getTime()) {
        return true;
      }

      return false;
    },
    message: 'The :attribute must be after :after.',
  },
  after_or_equal: {
    function: (val1: string, val2: string) => {
      if (!isValidDate(val1)) {
        return false;
      }

      if (!isValidDate(val2)) {
        return false;
      }

      if (new Date(val2).getTime() <= new Date(val1).getTime()) {
        return true;
      }

      return false;
    },
    message: 'The :attribute must be equal or after :after_or_equal.',
  },

  // Bugs found in validatorjs rules 'required_if' and 'required_unless' so overriding them here
  // At present they can not handle boolean values making them useless for checkboxes
  // Added toString() to condition values to resolve the problem
  // Issued raised - https://github.com/skaterdav85/validatorjs/issues/285
  required_if: {
    function: function(val: string, req: string): boolean {
      const params: Array<string> = this.getParameters();
      const v: Object = this.validator;
      // eslint-disable-next-line no-underscore-dangle
      const a: any = v._objectPath(v.input, params[0]);

      if (params[1].toString() === 'passConfirm') {
        if (a.toString().length > 0 && val.toString().length === 0) {
          return false;
        }
      }

      if (params[1].toString() === 'userRoleCheck') {
        if (
          (a === Roles.USER ||
            a === Roles.MANAGER ||
            a === Roles.PURCHASER ||
            a === Roles.VIEW_ONLY ||
            a === Roles.ACCOUNT_ADMIN) &&
          val.toString().length === 0
        ) {
          return false;
        }
      }

      if (params[1].toString() === 'regionUserRoleCheck') {
        if (
          (a === Roles.ACCOUNT_MANAGER ||
            a === Roles.ASSET_MANAGER ||
            a === Roles.BUSINESS_VIEW_ONLY ||
            a === Roles.SYSTEM_ADMIN) &&
          val.toString().length === 0
        ) {
          return false;
        }
      }

      if (a.toString() === params[1].toString()) {
        return v.getRule('required').validate(val);
      }

      return true;
    },
    message: 'The :attribute field is required.',
  },
  required_unless: {
    function: function(val: string, req: string): boolean {
      const params: Array<string> = this.getParameters();
      const v: Object = this.validator;
      // eslint-disable-next-line no-underscore-dangle
      const a: any = v._objectPath(v.input, params[0]);

      if (a.toString() !== params[1].toString()) {
        return v.getRule('required').validate(val);
      }

      return true;
    },
    message: 'The :attribute field is required.',
  },

  date_after_field: {
    function: function(val1: string, val2: string): boolean {
      const params: Array<string> = this.getParameters();
      const v: Object = this.validator;
      // eslint-disable-next-line no-underscore-dangle
      const a: any = v._objectPath(v.input, val2);

      if (!isValidDate(val1)) {
        return false;
      }

      if (new Date(a).getTime() < new Date(val1).getTime()) {
        return true;
      }

      return false;
    },
    message: 'The :attribute must be after start date.',
  },

  // Invalidates if 'asset_in_use' or 'asset_scheduled_use' warnings are true
  project_overlap: {
    function: (val1: Object) => {
      for (const key: string in val1) {
        if (val1[key] && (key === 'asset_in_use' || key === 'asset_scheduled_use')) {
          return false;
        }
      }

      return true;
    },
    message: 'The :attribute overlaps with another.',
  },
  file_extension: {
    function: (val: Object, req: string) => {
      return val.name && val.name.toLowerCase().substring(val.name.lastIndexOf('.') + 1) === req;
    },
    message: ':attribute must have the extension :file_extension.',
  },
  max_file_size: {
    function: (val: Object) => {
      return val.size && val.size <= 16000000;
    },
    message: ':attribute exceeds the maximum file size of 2Mb.',
  },
};

const validator: Object = ($validator: Object) => {
  // register async rules
  Object.keys(asyncRules).forEach((key: string) =>
    $validator.registerAsyncRule(key, asyncRules[key])
  );

  // register sync rules
  Object.keys(rules).forEach((key: string) =>
    $validator.register(key, rules[key].function, rules[key].message)
  );
};

export default validator;
